import { Box } from "@mui/material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  checkUnreadMessages,
  fetchChatRooms,
  updateChatRoomReadByUser,
} from "../firebase/chat";
import { db } from "../firebase/firebase";
import { setMessageBadge, setNotificationBadge } from "../redux/messagingSlice";
import { RootState } from "../redux/store";
import useSendMessage from "./useSendMessage";

/**
 * @description Check messages as well as notifications for unread messages
 *
 * @updated 05/26/2024: Added extended functionality to check
 * new Notification_Requests (i.e, Notifications) as well and
 * update the badges accordingly
 */
const useCheckUnreadMessages = (useLocation?: string) => {
  const userUid = useSelector((state: RootState) => state.user.user.uid);
  const dispatch = useDispatch();

  const { handleSendMessage } = useSendMessage();
  const selectedChatRoomId = useSelector(
    (state: RootState) => state.messaging.selectedChatId
  );

  const windowSize = window.innerWidth;

  useEffect(() => {
    if (!userUid || (useLocation === "BottomNavbar" && windowSize > 900)) {
      return;
    }

    const userRef = doc(db, "users", userUid);
    const unsubscribe = onSnapshot(userRef, async (docSnapshot) => {
      // console.error("CHAT SUBSCRIBER SNAPSHOT RAN", {
      //   userUid,
      //   selectedChatRoomId,
      // });

      if (!docSnapshot.exists()) {
        console.error("User document not found");
        return () => unsubscribe();
      }

      const userData = docSnapshot.data();
      const now = new Date();
      let lastChatUpdate = userData?.lastChatUpdate;
      let lastNotificationUpdate = userData?.lastNotificationUpdate;

      if (lastChatUpdate) {
        const chatRooms = await fetchChatRooms(userUid);
        if (
          selectedChatRoomId &&
          chatRooms.find((room) => room.id === selectedChatRoomId)
        ) {
          const hasUnreadMessages = checkUnreadMessages(
            userUid,
            chatRooms.find((room) => room.id === selectedChatRoomId)
          );
          if (hasUnreadMessages) {
            await updateChatRoomReadByUser(selectedChatRoomId, userUid);
          }
        } else {
          const hasUnreadMessages = chatRooms.some((room) =>
            checkUnreadMessages(userUid, room)
          );

          const chatRoomWithUnreadMessages = chatRooms.find((room) =>
            checkUnreadMessages(userUid, room)
          );

          if (hasUnreadMessages) {
            dispatch(setMessageBadge(false));
          }

          if (chatRoomWithUnreadMessages) {
            const messagesRef = collection(
              db,
              `chatRooms/${chatRoomWithUnreadMessages.id}/messages`
            );
            const latestMessageRef = query(
              messagesRef,
              orderBy("createdAt", "desc"),
              limit(1)
            );
            const messageSnapshot = await getDocs(latestMessageRef);
            const [messageData] = messageSnapshot.docs.map((doc) => {
              const result = doc.data();
              result.createdAt = result.createdAt.toDate().toISOString();
              return result;
            });

            if (
              messageData &&
              messageData.senderUid !== userUid &&
              messageData.type === "text"
            ) {
              if (
                now.getTime() - new Date(messageData.createdAt).getTime() <=
                6000
              ) {
                const senderRef = doc(db, "users", messageData.senderUid);
                const senderDoc = await getDoc(senderRef);
                const sender = senderDoc.data();

                if (sender) {
                  toast(
                    <Box
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        handleSendMessage(
                          messageData.senderUid,
                          messageData.receiverUid
                        )
                      }
                    >
                      <h5>New Message</h5>
                      <h6>
                        {sender.publicName ??
                          `${sender.firstName} ${sender.lastName}`}{" "}
                        sent you a new message.
                      </h6>
                    </Box>,
                    {
                      closeOnClick: false,
                    }
                  );
                }
              }
            }
          }
        }
      }

      /**
       * @deprecated
       * Notifications Deprecated: Stopped checking notifications for now
       */
      /* 
        const notificationRef = collection(
          db,
          "users",
          userUid,
          "notifications"
        );
        const notificationQuery = query(
          notificationRef,
          orderBy("sentTime", "desc"),
          limit(10)
        );
        const docsSnapshot = await getDocs(notificationQuery);
        const docs = docsSnapshot.docs.map((doc) => doc.data());

        if (!docs.every((doc) => !!doc.isRead)) {
          dispatch(setNotificationBadge(false));
        
        }
      */
    });

    return () => unsubscribe();
  }, [userUid, selectedChatRoomId]);

  return null;
};

export default useCheckUnreadMessages;
